import { getTimelinePortCalls } from './shipment';
import type { Leg } from '../core/TimeData';
import { CoreEventType } from '../core/TimeData';
import LeafletMap from '../windy/leafletMap';
import React, { useEffect, useMemo, useState } from 'react';
import type { EventTimeline } from '../core/Timelines';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import type {
  Maybe,
  RouteByDraftAndCoordinatesAndAvoidsChannelsQuery,
  RouteByDraftAndCoordinatesAndAvoidsChannelsQueryVariables,
} from '../../graphql';
import {
  RouteByDraftAndCoordinatesAndAvoidsChannelsDocument,
  useAllNoGoZonesQuery,
} from '../../graphql';
import { splitLineString } from './lines';
import type { ScenarioLocation } from '../../lib/planningData';
import { nonEmptyArrayOrNull } from '../distances/DistanceProvider';
import { isDefined } from '../../lib/util';

type RouteData = {
  path: { lat: number; lng: number }[];
  from: string;
  to: string;
};

type RouteParams = {
  avoidsChannels: boolean;
  toLat: string | number;
  toLong: string | number;
  fromLat: string | number;
  draft: string | number | undefined | null;
  fromLong: string | number;
  noGoZones: number[] | null;
};

async function fetchAndProcessRoute(
  client: ApolloClient<object>,
  variables: RouteParams,
  from: ScenarioLocation,
  to: ScenarioLocation,
) {
  const result = await client.query<
    RouteByDraftAndCoordinatesAndAvoidsChannelsQuery,
    RouteByDraftAndCoordinatesAndAvoidsChannelsQueryVariables
  >({
    query: RouteByDraftAndCoordinatesAndAvoidsChannelsDocument,
    fetchPolicy: 'no-cache',
    variables,
  });
  if (result.error) throw result.error;
  if (result.errors) throw result.errors;
  const decomposed =
    result.data.routeByDraftAndCoordinatesAndAvoidsChannels?.decomposed;
  const json = decomposed && JSON.parse(decomposed);
  const coordinatePoints = json?.coordinatePoints;
  if (!coordinatePoints) {
    return null;
  }
  return splitLineString({
    type: 'LineString',
    coordinates: coordinatePoints.map(
      (
        point:
          | { latitude: number; longitude: number }
          /**
           * FIXME: Legacy data format of routes with uppercase properties (Longitude and Latitude) were inserted
           * from this ticket: https://github.com/Seaber-io/frontend/pull/3192
           *
           * This is a workaround (https://github.com/Seaber-io/frontend/pull/3711)
           * to support both formats until the legacy data is cleaned up
           *
           * This fallback can be removed once stale data rows in the database
           */
          | { Latitude: number; Longitude: number },
      ) =>
        'longitude' in point
          ? [point.longitude, point.latitude]
          : [point.Longitude, point.Latitude],
    ),
  }).map((part): RouteData => {
    const path: {
      lat: number;
      lng: number;
    }[] = part.coordinates.map(([longitude, latitude]) => ({
      lat: +latitude,
      lng: +longitude,
    }));
    return {
      path,
      from: from.name ?? '',
      to: to.name ?? '',
    };
  });
}
const cache: {
  [key: string]: ReturnType<typeof fetchAndProcessRoute> | RouteData[] | null;
} = {};
const getRoutes = async (
  legs: Leg[],
  draft: string | number | undefined | null,
  noGoZones: number[] | null,
  client: ApolloClient<object>,
): Promise<RouteData[]> => {
  return (
    await Promise.all(
      legs.map((leg) => {
        const { from, to, avoidsChannels } = leg;
        if (!from || !to) return null;
        const variables: RouteParams = {
          fromLat: from.latitude || -1,
          fromLong: from.longitude || -1,
          toLat: to.latitude || -1,
          toLong: to.longitude || -1,
          avoidsChannels,
          draft,
          noGoZones,
        };
        const key = JSON.stringify(variables);
        const cached = cache[key];
        if (cached !== undefined) {
          return cached;
        }
        const promise = fetchAndProcessRoute(client, variables, from, to);
        cache[key] = promise;
        promise
          .then((result) => {
            cache[key] = result;
          })
          .catch(() => delete cache[key]);
        return promise;
      }),
    )
  )
    .flat()
    .filter((r): r is RouteData => Boolean(r && r.path && r.from && r.to));
};

export const ShipmentMap = ({
  timeline,
  draft,
}: {
  timeline: EventTimeline;
  draft: Maybe<string | number> | undefined;
}) => {
  const { legs, locationById, portCalls } = useMemo(
    () => getTimelinePortCalls(timeline),
    [timeline],
  );
  const events = useMemo(
    () =>
      portCalls
        .map((pc) => {
          const hasLoad = pc.tds.some(
            (td) => td.event.type === CoreEventType.Load,
          );
          const hasDischarge = pc.tds.some(
            (td) => td.event.type === CoreEventType.Discharge,
          );
          const location = locationById.get(pc.locationId);
          return {
            latitude: location?.latitude,
            longitude: location?.longitude,
            name: location?.name ?? null,
            hasLoad,
            hasDischarge,
          };
        })
        .filter(
          (
            pc,
          ): pc is {
            latitude: string | number;
            longitude: string | number;
            name: string | null;
            hasLoad: boolean;
            hasDischarge: boolean;
          } => {
            const val = pc.latitude && pc.longitude && pc.name;
            return val !== null && val !== undefined;
          },
        ),
    [portCalls, locationById],
  );
  const client = useApolloClient();
  const [routes, setRoutes] = useState<RouteData[]>();
  const allNoGoZones = useAllNoGoZonesQuery();
  const noGoZoneNodes = allNoGoZones.data?.allNoGoZones?.nodes;
  const noGoZones = useMemo(
    () =>
      nonEmptyArrayOrNull(
        noGoZoneNodes
          ?.map((n) => n?.id)
          .filter(isDefined)
          .sort((a, b) => a - b) ?? null,
      ),
    [noGoZoneNodes],
  );
  const polygons = useMemo(
    () =>
      noGoZoneNodes
        ?.map(
          (n) =>
            n?.polygon && {
              name: n.name,
              polygon: n.polygon.filter(
                (p): p is { lat: number; lng: number } =>
                  (p && p.lat && p.lng) != null,
              ),
            },
        )
        .filter(isDefined),
    [noGoZoneNodes],
  );
  useEffect(() => {
    getRoutes(legs, draft, noGoZones, client).then(setRoutes);
  }, [legs, draft, noGoZones, client]);

  return <LeafletMap events={events} routes={routes} polygons={polygons} />;
};
